"use client";

import * as React from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  BookOpen,
  ChevronLeft,
  ChevronsUpDown,
  Crown,
  MessageSquare,
  Network,
  Paperclip,
  Search,
  Settings2,
  Sparkles,
  Users,
  Wand2,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { spacesApi } from "@/lib/api/spaces";
import { filesApi } from "@/lib/api/files";
import { documentTemplatesApi } from "@/lib/api/document-templates";
import { forgeApi } from "@/lib/api/forge";
import type { JobStatus } from "@/types";
import { useAuthStore } from "@/stores/auth-store";
import { useUIStore } from "@/stores/ui-store";
import { BRANDING } from "@/config/branding";
import { cn } from "@/lib/utils/cn";
import { FILE_ROLE_LABELS, SPACE_STATUS_LABELS, type FileRole } from "@/types/space";
import type { FileRecord } from "@/types/file";

interface SpaceSidebarProps {
  spaceId: number;
}

interface SidebarItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

export function SpaceSidebar({ spaceId }: SpaceSidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { sidebarCollapsed, toggleSidebarCollapsed } = useUIStore();
  const { accessToken, _hasHydrated } = useAuthStore();
  const isAuthenticated = _hasHydrated && !!accessToken;

  const { data: space } = useQuery({
    queryKey: ["space", spaceId],
    queryFn: () => spacesApi.get(spaceId),
    enabled: !isNaN(spaceId) && isAuthenticated,
  });

  const { data: files } = useQuery({
    queryKey: ["space-files", spaceId],
    queryFn: () => filesApi.getAll({ spaceId, pageSize: 100 }),
    enabled: !isNaN(spaceId) && isAuthenticated,
  });

  const { data: templates = [] } = useQuery({
    queryKey: ["document-templates"],
    queryFn: () => documentTemplatesApi.list(false),
    enabled: isAuthenticated,
  });

  const { data: allSpaces } = useQuery({
    queryKey: ["spaces"],
    queryFn: () => spacesApi.list({ pageSize: 50 }),
    enabled: isAuthenticated,
  });

  const { data: forgeJobs = [] } = useQuery({
    queryKey: ["forge-jobs", spaceId],
    queryFn: () => forgeApi.list(spaceId, 50),
    enabled: isAuthenticated && !isNaN(spaceId),
    refetchInterval: 5000,
  });
  const IN_PROGRESS: JobStatus[] = ["queued", "planning", "generating", "reviewing", "rendering"];
  const inProgressCount = forgeJobs.filter((j) => IN_PROGRESS.includes(j.status)).length;

  const templateName = React.useMemo(() => {
    if (!space?.documentTemplateId) return null;
    return templates.find((t) => t.id === space.documentTemplateId)?.name ?? null;
  }, [space, templates]);

  const countByRole = React.useMemo<Record<FileRole, number>>(() => {
    const counts: Record<FileRole, number> = {
      source: 0, reference: 0, generated: 0, attachment: 0,
    };
    for (const f of (files?.items || []) as FileRecord[]) {
      const role = (f as unknown as Record<string, unknown>).role as string ||
        f.documentType || "attachment";
      if (role in counts) counts[role as FileRole] += 1;
    }
    return counts;
  }, [files]);

  const contentItems: SidebarItem[] = [
    { href: `/spaces/${spaceId}/source`, label: FILE_ROLE_LABELS.source, icon: Search, badge: countByRole.source },
    { href: `/spaces/${spaceId}/reference`, label: FILE_ROLE_LABELS.reference, icon: BookOpen, badge: countByRole.reference },
    { href: `/spaces/${spaceId}/attachment`, label: FILE_ROLE_LABELS.attachment, icon: Paperclip, badge: countByRole.attachment },
  ];

  const isActive = (href: string) =>
    pathname === href || pathname.startsWith(`${href}/`);

  const forgeHref = `/spaces/${spaceId}/forge`;
  const forgeActive = isActive(forgeHref);

  const toolItems: SidebarItem[] = [
    { href: `/spaces/${spaceId}/wiki`, label: "Wiki", icon: Network },
    { href: `/spaces/${spaceId}/chat`, label: "Chat", icon: MessageSquare },
    { href: `/spaces/${spaceId}/members`, label: "Miembros", icon: Users },
  ];

  const myRole = space?.myRole;
  const roleBadgeLabel =
    myRole === "owner"
      ? "Propietario"
      : myRole === "collaborator"
        ? "Colaborador"
        : myRole === "superadmin"
          ? "Superadmin"
          : null;

  return (
    <aside
      className={cn(
        "layout__sidebar sidebar",
        sidebarCollapsed && "layout__sidebar--slim sidebar--slim"
      )}
    >
      {/* Brand + collapse */}
      <div className="sidebar__section flex items-center justify-between border-b border-[var(--gray-200)]">
        <Link href="/" className="sidebar__brand flex items-center gap-2">
          <Image
            src={BRANDING.LOGO_PATH}
            alt={BRANDING.LOGO_ALT}
            width={BRANDING.LOGO_SIDEBAR_WIDTH}
            height={BRANDING.LOGO_SIDEBAR_HEIGHT}
            priority
            className="object-contain"
          />
          {!sidebarCollapsed && (
            <span className="text-lg font-bold tracking-tight" style={{ color: "var(--gray-900)" }}>
              {BRANDING.APP_NAME}
            </span>
          )}
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebarCollapsed}
          className={cn("h-8 w-8", sidebarCollapsed && "mx-auto")}
          aria-label={sidebarCollapsed ? "Expandir menu" : "Colapsar menu"}
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform duration-300",
              sidebarCollapsed && "rotate-180"
            )}
          />
        </Button>
      </div>

      {/* Breadcrumb volver */}
      <div className="sidebar__section border-b border-[var(--gray-200)]">
        {(() => {
          const backLink = (
            <Link
              href="/spaces"
              className={cn(
                "sidebar-menu__entry__link",
                sidebarCollapsed && "justify-center px-2"
              )}
            >
              <ArrowLeft className="sidebar-menu__entry__icon" />
              {!sidebarCollapsed && <span>Espacios</span>}
            </Link>
          );
          return sidebarCollapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>{backLink}</TooltipTrigger>
              <TooltipContent side="right">Volver a espacios</TooltipContent>
            </Tooltip>
          ) : backLink;
        })()}
        {!sidebarCollapsed && space && (
          <div className="mt-2 px-3 space-y-1">
            <p className="text-sm font-semibold truncate" style={{ color: "var(--gray-900)" }} title={space.name}>
              {space.name}
            </p>
            <div className="flex items-center gap-1.5 flex-wrap">
              <Badge
                variant={space.status === "active" ? "default" : "secondary"}
                className="text-[9px] px-1.5 py-0 h-4 leading-none"
              >
                {SPACE_STATUS_LABELS[space.status as keyof typeof SPACE_STATUS_LABELS] || space.status}
              </Badge>
              {roleBadgeLabel && (
                <Badge
                  variant="outline"
                  className={cn(
                    "text-[9px] px-1.5 py-0 h-4 leading-none gap-0.5",
                    myRole === "owner" && "border-amber-500 text-amber-700",
                    myRole === "collaborator" && "border-blue-500 text-blue-700",
                    myRole === "superadmin" && "border-purple-500 text-purple-700",
                  )}
                  title={`Tu rol en este espacio: ${roleBadgeLabel}`}
                >
                  {myRole === "owner" && <Crown className="h-2.5 w-2.5" />}
                  {roleBadgeLabel}
                </Badge>
              )}
              {space.category && (
                <span className="text-[10px] text-muted-foreground truncate" title={space.category.name}>
                  {space.category.name}
                </span>
              )}
            </div>
            {space.case && (
              <p
                className="text-[10px] truncate"
                style={{ color: space.case.color || "var(--gray-500)" }}
                title={space.case.label}
              >
                Caso: {space.case.label}
              </p>
            )}
            {templateName && !space.case && (
              <p className="text-[10px] text-muted-foreground truncate" title={templateName}>
                {templateName}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Navigation */}
      <ScrollArea className="sidebar__section--fluid">
        <nav className="sidebar-menu">
          {/* Forja: accion principal del espacio */}
          <div className="sidebar-menu__entry px-2 pt-2">
            {(() => {
              const forgeLink = (
                <Link
                  href={forgeHref}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors",
                    forgeActive
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-primary/10 text-primary hover:bg-primary/20",
                    sidebarCollapsed && "justify-center px-2"
                  )}
                >
                  <Sparkles className="h-4 w-4 flex-shrink-0" />
                  {!sidebarCollapsed && (
                    <>
                      <span className="flex-1">Forja</span>
                      {inProgressCount > 0 && (
                        <Badge
                          variant="secondary"
                          className="ml-auto h-5 text-[10px] px-1.5 bg-background/80 text-foreground"
                        >
                          {inProgressCount}
                        </Badge>
                      )}
                    </>
                  )}
                </Link>
              );
              return sidebarCollapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>{forgeLink}</TooltipTrigger>
                  <TooltipContent side="right">
                    Forja de Documentos{inProgressCount > 0 ? ` · ${inProgressCount} en curso` : ""}
                  </TooltipContent>
                </Tooltip>
              ) : forgeLink;
            })()}
          </div>

          {!sidebarCollapsed && (
            <p className="mt-4 mb-2 px-4 pt-2 text-xs font-semibold uppercase" style={{ color: "var(--gray-500)" }}>
              Contenido
            </p>
          )}
          {contentItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            const link = (
              <Link
                href={item.href}
                className={cn(
                  "sidebar-menu__entry__link",
                  active && "active",
                  sidebarCollapsed && "justify-center px-2"
                )}
              >
                <Icon className="sidebar-menu__entry__icon" />
                {!sidebarCollapsed && (
                  <>
                    <span className="flex-1">{item.label}</span>
                    {item.badge !== undefined && item.badge > 0 && (
                      <Badge variant="secondary" className="ml-auto h-5 text-[10px] px-1.5">
                        {item.badge}
                      </Badge>
                    )}
                  </>
                )}
              </Link>
            );
            return (
              <div key={item.href} className="sidebar-menu__entry">
                {sidebarCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right">
                      {item.label}
                      {item.badge !== undefined && item.badge > 0 ? ` (${item.badge})` : ""}
                    </TooltipContent>
                  </Tooltip>
                ) : link}
              </div>
            );
          })}

          {!sidebarCollapsed && (
            <p className="mt-4 mb-2 px-4 pt-2 text-xs font-semibold uppercase" style={{ color: "var(--gray-500)" }}>
              Herramientas
            </p>
          )}
          {toolItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            const link = (
              <Link
                href={item.href}
                className={cn(
                  "sidebar-menu__entry__link",
                  active && "active",
                  sidebarCollapsed && "justify-center px-2"
                )}
              >
                <Icon className="sidebar-menu__entry__icon" />
                {!sidebarCollapsed && <span>{item.label}</span>}
              </Link>
            );
            return (
              <div key={item.href} className="sidebar-menu__entry">
                {sidebarCollapsed ? (
                  <Tooltip>
                    <TooltipTrigger asChild>{link}</TooltipTrigger>
                    <TooltipContent side="right">{item.label}</TooltipContent>
                  </Tooltip>
                ) : link}
              </div>
            );
          })}
        </nav>
      </ScrollArea>

      {/* Footer: ajustes + cambiar espacio */}
      <div className="sidebar__section border-t border-[var(--gray-200)] space-y-1">
        <div className="sidebar-menu__entry">
          {(() => {
            const settingsLink = (
              <Link
                href={`/spaces/${spaceId}/settings`}
                className={cn(
                  "sidebar-menu__entry__link",
                  isActive(`/spaces/${spaceId}/settings`) && "active",
                  sidebarCollapsed && "justify-center px-2"
                )}
              >
                <Settings2 className="sidebar-menu__entry__icon" />
                {!sidebarCollapsed && <span>Ajustes</span>}
              </Link>
            );
            return sidebarCollapsed ? (
              <Tooltip>
                <TooltipTrigger asChild>{settingsLink}</TooltipTrigger>
                <TooltipContent side="right">Ajustes del espacio</TooltipContent>
              </Tooltip>
            ) : settingsLink;
          })()}
        </div>

        {!sidebarCollapsed && allSpaces && allSpaces.items.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="sidebar-menu__entry__link w-full justify-between text-xs text-muted-foreground hover:text-foreground">
                <span className="flex items-center gap-2">
                  <Wand2 className="h-3.5 w-3.5" />
                  Cambiar espacio
                </span>
                <ChevronsUpDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-64 max-h-[400px] overflow-auto">
              <DropdownMenuLabel>Saltar a otro espacio</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {allSpaces.items
                .filter((s) => s.id !== spaceId)
                .map((s) => (
                  <DropdownMenuItem
                    key={s.id}
                    onSelect={() => router.push(`/spaces/${s.id}/source`)}
                  >
                    {s.name}
                  </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </aside>
  );
}
